import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Table,
  Container,
  Row,
  Col,
  Input,
  CardFooter,
} from "reactstrap";
import Header from "components/Headers/Header";
import { useSessionStore } from "../../stores/sessionStore";
import { API_GETSERVICES, API_DELSERVICE } from "../../config/api";
import { useTablePaging, PAGE_SIZE_OPTIONS } from "../../stores/pagingStore";
import { api } from "../../config/middleware";
import { useAuth } from "../../auth/AuthContext";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import LoadingSpinner from "components/LoadingSpinner";

const TABLE_KEY = "services";

const ManagePackage = () => {
  const MySwal = withReactContent(Swal);

  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (
    user?.role ||
    localStorage.getItem("role") ||
    "user"
  ).toLowerCase(); // (เผื่อใช้)
  const orgId = useSessionStore((s) => s.profile?.orgId);

  // ✅ responsive: จอเล็กใช้ table เดิม
  const [isSmall, setIsSmall] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // paging (persisted via zustand)
  const { page, pageSize, setPage, setPageSize } = useTablePaging(TABLE_KEY);

  // server/client paging state
  const [total, setTotal] = useState(0);
  const [rowsPage, setRowsPage] = useState([]); // rows of current page (server-paged)
  const [allRows, setAllRows] = useState([]); // all rows (client-paged)
  const [clientPaged, setClientPaged] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [keyword, setKeyword] = useState("");
  const [deletingId, setDeletingId] = useState(null); // current deleting svId

  const reqIdRef = useRef(0);
  const serverModeRef = useRef(false);

  // helpers
  const getId = (it, i) =>
    it.svId ?? it.ID ?? `${it.svCode ?? it.code ?? "row"}-${i}`;
  const dedupeBy = (arr, idFn) => {
    const m = new Map();
    arr.forEach((x, i) => m.set(idFn(x, i), x));
    return [...m.values()];
  };

  const pickTotal = (d) =>
    Number(
      d?.total ?? d?.count ?? d?.pagination?.total ?? d?.meta?.total ?? NaN,
    );
  const norm = (s) => (s || "").toString().toLowerCase().trim();

  // base rows (ตามโหมด)
  const baseRows = clientPaged ? allRows : rowsPage;

  // filter ด้วย keyword
  const filteredRows = useMemo(() => {
    const k = norm(keyword);
    if (!k) return baseRows;
    return baseRows.filter(
      (r) =>
        norm(r.svCode ?? r.code ?? "").includes(k) ||
        norm(r.svName ?? r.name ?? "").includes(k) ||
        norm(r.svType ?? "").includes(k),
    );
  }, [baseRows, keyword]);

  const filteredTotal = filteredRows.length;
  const filteredPageCount = Math.max(1, Math.ceil(filteredTotal / pageSize));

  // rows ที่แสดงในหน้า
  const displayRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  // fetch หน้าแรก เพื่อตรวจว่า API รองรับ server paging ไหม
  const fetchFirstPage = async () => {
    if (!orgId) return;
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    setError("");
    setClientPaged(false);
    serverModeRef.current = false;
    setRowsPage([]);
    setAllRows([]);
    setTotal(0);
    setPage(TABLE_KEY, 1);

    try {
      const res = await api.get(API_GETSERVICES, {
        params: { ORGID: orgId, PAGE: 1, LIMIT: pageSize },
      });

      if (myReq !== reqIdRef.current) return;
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      const dedup = dedupeBy(raw, getId);
      const totalFromApi = pickTotal(res.data);
      const apiLooksPaged =
        Number.isFinite(totalFromApi) && dedup.length <= pageSize;

      if (apiLooksPaged) {
        // server-paged
        setClientPaged(false);
        serverModeRef.current = true;
        setRowsPage(dedup);
        setTotal(totalFromApi);
      } else {
        // client-paged
        serverModeRef.current = false;
        setClientPaged(true);
        const all = dedupeBy(raw, getId);
        setAllRows(all);
        setTotal(all.length);
      }
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      setError(e?.message || "โหลดบริการไม่สำเร็จ");
    } finally {
      if (myReq === reqIdRef.current) setIsLoading(false);
    }
  };

  // fetch หน้า N (เฉพาะ server-paged)
  const fetchServerPage = async (nextPage) => {
    if (!serverModeRef.current || clientPaged) return;
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    setError("");
    try {
      const res = await api.get(API_GETSERVICES, {
        params: { ORGID: orgId, PAGE: nextPage, LIMIT: pageSize },
      });
      if (myReq !== reqIdRef.current) return;
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      const dedup = dedupeBy(raw, getId);
      setRowsPage(dedup);
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      setError(e?.message || "โหลดบริการไม่สำเร็จ");
    } finally {
      if (myReq === reqIdRef.current) setIsLoading(false);
    }
  };

  // ดึงทั้งหมดเพื่อค้นหาในโหมด server
  const fetchAllForSearch = async () => {
    if (!orgId) return;
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    setError("");
    try {
      const first = await api.get(API_GETSERVICES, {
        params: { ORGID: orgId, PAGE: 1, LIMIT: pageSize },
      });
      if (myReq !== reqIdRef.current) return;

      const firstRows = Array.isArray(first.data?.data) ? first.data.data : [];
      const totalAll = pickTotal(first.data) || firstRows.length;
      const pages = Math.max(1, Math.ceil(totalAll / pageSize));
      let all = dedupeBy(firstRows, getId);

      for (let p = 2; p <= pages; p++) {
        const r = await api.get(API_GETSERVICES, {
          params: { ORGID: orgId, PAGE: p, LIMIT: pageSize },
        });
        if (myReq !== reqIdRef.current) return;
        const part = Array.isArray(r.data?.data) ? r.data.data : [];
        all = dedupeBy([...all, ...part], getId);
      }

      setClientPaged(true);
      serverModeRef.current = true;
      setAllRows(all);
      setTotal(all.length);
      setPage(TABLE_KEY, 1);
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      setError(e?.message || "โหลดบริการไม่สำเร็จ");
    } finally {
      if (myReq === reqIdRef.current) setIsLoading(false);
    }
  };

  // lifecycle
  useEffect(() => {
    fetchFirstPage(); /* eslint-disable-next-line */
  }, [orgId]);
  useEffect(() => {
    if (!clientPaged) fetchServerPage(page); /* eslint-disable-next-line */
  }, [page]);
  useEffect(() => {
    setKeyword("");
    fetchFirstPage();
    /* eslint-disable-next-line */
  }, [pageSize]);

  useEffect(() => {
    setPage(TABLE_KEY, 1);
    if (keyword && !clientPaged) fetchAllForSearch();
    if (!keyword && serverModeRef.current && clientPaged) fetchFirstPage();
    /* eslint-disable-next-line */
  }, [keyword]);

  // ป้องกันหน้าเกินเมื่อกรอง/เปลี่ยน pageSize
  useEffect(() => {
    if (filteredPageCount && page > filteredPageCount) {
      setPage(TABLE_KEY, filteredPageCount);
    }
  }, [filteredPageCount, page, setPage]);

  const handleAddPackage = () => {
    navigate("/add-package");
  };

  // ===== ลบบริการ =====
  const handleDelete = async (sv) => {
    if (!orgId) return;

    const { isConfirmed } = await MySwal.fire({
      title: `ยืนยันลบบริการ`,
      html: `<div style="text-align:center">
        <div><b>รหัสบริการ:</b> ${sv.svCode ?? "-"}</div>
        <div><b>ชื่อบริการ:</b> ${sv.svName ?? "-"}</div>
        <div><b>ประเภท:</b> ${sv.svType ?? "-"}</div>
      </div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#e74c3c",
      cancelButtonColor: "#6c757d",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!isConfirmed) return;

    try {
      setDeletingId(sv.svId);
      setError("");

      await api.post(API_DELSERVICE, {
        SVID: sv.svId,
        SVCODE: sv.svCode,
        ORGID: orgId,
      });

      // อัปเดต UI หลังลบ
      if (clientPaged) {
        setAllRows((rows) => rows.filter((r) => r.svId !== sv.svId));
        setTotal((t) => Math.max(0, t - 1));
      } else {
        setTotal((t) => Math.max(0, t - 1));
        await fetchServerPage(page);
      }

      await MySwal.fire({
        icon: "success",
        title: "ลบสำเร็จ",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (e) {
      const msg = e?.message || "ลบรายการไม่สำเร็จ";
      setError(msg);
      await MySwal.fire({
        icon: "error",
        title: "ลบไม่สำเร็จ",
        text: msg,
      });
    } finally {
      setDeletingId(null);
    }
  };

  // ===== สร้างหมายเลขหน้าแบบย่อ =====
  const buildPages = (p, max) => {
    const pages = [];
    const start = Math.max(1, p - 1);
    const end = Math.min(max, p + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (start > 1) pages.unshift(1);
    if (end < max) pages.push(max);
    return [...new Set(pages)];
  };
  const pages = buildPages(page, filteredPageCount);

  if (isLoading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="border-0 d-flex align-items-center justify-content-between">
                <h6 className="mb-0">บริการ</h6>
                <Button color="success" onClick={handleAddPackage}>
                  เพิ่มบริการ
                </Button>
              </CardHeader>

              <CardBody>
                {/* Toolbar */}
                <div className="mb-3">
                  <Row className="align-items-center">
                    <Col
                      xs="12"
                      md="6"
                      className="d-flex align-items-center"
                      style={{ gap: 8 }}
                    >
                      <Input
                        type="select"
                        value={pageSize}
                        onChange={(e) =>
                          setPageSize(TABLE_KEY, Number(e.target.value))
                        }
                        style={{ width: 96 }}
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Input>
                      <span className="text-muted">แถว/หน้า</span>
                    </Col>
                    <Col
                      xs="12"
                      md="6"
                      className="mt-2 mt-md-0 d-flex justify-content-md-end"
                    >
                      <Input
                        type="text"
                        placeholder="ค้นหา รหัสบริการ/ชื่อบริการ/ประเภท"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        className="table-modern-search w-100 w-md-auto"
                        style={{ maxWidth: 320, minWidth: 280 }}
                      />
                    </Col>
                  </Row>
                </div>

                {/* ✅ TABLE: เล็ก=เดิม, กลาง/ใหญ่=แบบรูป */}
                {isSmall ? (
                  <div className="table-responsive">
                    <Table hover className="table table-modern mb-0">
                      <thead className="thead-colored">
                        <tr>
                          <th className="text-start">รหัสบริการ</th>
                          <th className="text-start">ชื่อบริการ</th>
                          <th className="text-start">ประเภท</th>
                          <th className="text-start">จำนวน</th>
                          <th className="text-start">หน่วย</th>
                          <th className="text-start">ราคา (บาท)</th>
                          <th className="text-start">จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading && (
                          <tr>
                            <td colSpan={7} className="text-center py-4">
                              กำลังโหลด...
                            </td>
                          </tr>
                        )}
                        {!isLoading && error && (
                          <tr>
                            <td
                              colSpan={7}
                              className="text-center text-danger py-4"
                            >
                              {error}
                            </td>
                          </tr>
                        )}
                        {!isLoading && !error && displayRows.length === 0 && (
                          <tr>
                            <td colSpan={7} className="text-center py-4">
                              ไม่พบรายการ
                            </td>
                          </tr>
                        )}
                        {!isLoading &&
                          !error &&
                          displayRows.map((sv, i) => (
                            <tr key={getId(sv, i)}>
                              <td data-th="รหัสบริการ">{sv.svCode}</td>
                              <td data-th="ชื่อบริการ">{sv.svName}</td>
                              <td data-th="ประเภท">{sv.svType}</td>
                              <td data-th="จำนวน">{sv.svQty}</td>
                              <td data-th="หน่วย">{sv.svUnit}</td>
                              <td data-th="ราคา (บาท)">{Number(sv.svPrice ?? 0)}</td>
                              <td className="text-start" data-th="จัดการ">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="warning"
                                    size="sm"
                                    onClick={() =>
                                      navigate("/edit-package", {
                                        state: { sv },
                                      })
                                    }
                                  >
                                    แก้ไข
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleDelete(sv)}
                                    disabled={deletingId === sv.svId}
                                  >
                                    {deletingId === sv.svId
                                      ? "กำลังลบ..."
                                      : "ลบ"}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="zi-table-wrap">
                    <Table responsive hover className="zi-table mb-0">
                      <thead>
                        <tr>
                          <th className="text-start" style={{ width: 120 }}>
                            รหัสบริการ
                          </th>
                          <th className="text-start" style={{ width: 200 }}>
                            ชื่อบริการ
                          </th>
                          <th className="text-start" style={{ width: 120 }}>
                            ประเภท
                          </th>
                          <th className="text-start" style={{ width: 120 }}>
                            จำนวน
                          </th>
                          <th className="text-start" style={{ width: 120 }}>
                            หน่วย
                          </th>
                          <th className="text-start" style={{ width: 120 }}>
                            ราคา (บาท)
                          </th>
                          <th className="text-end" style={{ width: 120 }}>
                            การจัดการ
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td
                              colSpan={7}
                              className="text-center text-muted py-4"
                            >
                              กำลังโหลด...
                            </td>
                          </tr>
                        ) : error ? (
                          <tr>
                            <td
                              colSpan={7}
                              className="text-center text-danger py-4"
                            >
                              {error}
                            </td>
                          </tr>
                        ) : displayRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={7}
                              className="text-center text-muted py-4"
                            >
                              ไม่พบรายการ
                            </td>
                          </tr>
                        ) : (
                          displayRows.map((sv, i) => (
                            <tr key={getId(sv, i)}>
                              <td className="text-start">{sv.svCode}</td>
                              <td className="text-start fw-600">{sv.svName}</td>
                              <td className="text-start">{sv.svType}</td>
                              <td className="text-start">{sv.svQty}</td>
                              <td className="text-start">{sv.svUnit}</td>
                              <td className="text-start">
                                {Number(sv.svPrice ?? 0)}
                              </td>
                              <td className="text-end">
                                <div className="d-inline-flex gap-2">
                                  <Button
                                    color="warning"
                                    size="sm"
                                    onClick={() =>
                                      navigate("/edit-package", {
                                        state: { sv },
                                      })
                                    }
                                  >
                                    แก้ไข
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleDelete(sv)}
                                    disabled={deletingId === sv.svId}
                                  >
                                    {deletingId === sv.svId
                                      ? "กำลังลบ..."
                                      : "ลบ"}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Summary */}
                <div className="d-flex justify-content-end justify-content-sm-end justify-content-center mt-2">
                  <div className="text-muted small">
                    แสดง {(page - 1) * pageSize + (displayRows.length ? 1 : 0)}–
                    {Math.min(page * pageSize, filteredTotal)} จากทั้งหมด{" "}
                    {filteredTotal} รายการ
                  </div>
                </div>

                {/* ✅ CSS desktop/tablet table */}
                <style>{`
                  .zi-table-wrap{
                    background:#fff;
                    border-radius:10px;
                    border:1px solid rgba(0,0,0,.06);
                    overflow:hidden;
                  }
                  .zi-table{ margin:0; }
                  .zi-table thead th{
                    background:#f7fafc;
                    color:#0f172a;
                    font-weight:700;
                    font-size:14px;
                    border-bottom:1px solid rgba(0,0,0,.06);
                    padding:14px 16px;
                    white-space:nowrap;
                  }
                  .zi-table tbody td{
                    padding:14px 16px;
                    border-top:1px solid rgba(0,0,0,.06);
                    vertical-align:middle;
                    font-size:14px;
                    color:#0f172a;
                  }
                  .zi-table tbody tr:hover{ background:#f9fbff; }
                  .fw-600{ font-weight:600; }
                `}</style>
              </CardBody>

              {/* Pagination */}
              {filteredTotal > 0 && (
                <CardFooter
                  className="py-4"
                  style={{ position: "relative", zIndex: 10 }}
                >
                  <div className="paging-wrap">
                    <button
                      className="paging-btn icon"
                      onClick={() => setPage(TABLE_KEY, Math.max(1, page - 1))}
                      disabled={page === 1}
                      aria-label="Previous page"
                    >
                      ‹
                    </button>

                    {pages.map((p) => (
                      <button
                        key={p}
                        className={`paging-btn ${page === p ? "active" : ""}`}
                        onClick={() => setPage(TABLE_KEY, p)}
                      >
                        {p}
                      </button>
                    ))}

                    <button
                      className="paging-btn icon"
                      onClick={() =>
                        setPage(
                          TABLE_KEY,
                          Math.min(filteredPageCount, page + 1),
                        )
                      }
                      disabled={page === filteredPageCount}
                      aria-label="Next page"
                    >
                      ›
                    </button>
                  </div>
                </CardFooter>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ManagePackage;
